import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Compass } from "lucide-react";
import Masthead from "@/components/alcott/Masthead";
import AlcottFooter from "@/components/alcott/AlcottFooter";
import HorizonWaitlist from "@/components/alcott/HorizonWaitlist";
import { HORIZON_TIERS, IMG } from "@/data/alcott";

const ease = [0.22, 1, 0.36, 1];

export const Horizon = () => {
  const navigate = useNavigate();
  const [waitlist, setWaitlist] = useState(null); // context string or null

  return (
    <div className="min-h-screen bg-[#F4EFE6] font-body text-[#0A0A0A]" data-testid="horizon-page">
      <Masthead />

      {/* Cinematic hero */}
      <section className="relative h-[78vh] min-h-[520px] w-full overflow-hidden" data-testid="horizon-hero">
        <motion.img
          src={IMG.horizonFeature}
          alt="The Horizon Circle"
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/30 to-[#0A0A0A]/20" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1500px] flex-col justify-end px-5 pb-16 md:px-10 md:pb-24">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8, ease }}
            className="font-body text-[0.7rem] uppercase tracking-[0.3em] text-[#F4EFE6]/80"
          >
            The Horizon Circle
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.9, ease }}
            className="mt-4 max-w-3xl font-serif text-[clamp(2.6rem,7vw,6rem)] font-semibold leading-[0.95] tracking-tight text-[#FAF9F6]"
          >
            Where culture meets global mobility.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9, ease }}
            className="mt-6 max-w-xl font-body text-base leading-relaxed text-[#FAF9F6]/70"
          >
            The premier travel and lifestyle sanctuary for the modern vanguard — a society
            of curated experiences, private itineraries, and quiet global belonging.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9, ease }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link to="/experiences" className="rounded-full bg-[#C05C42] px-7 py-3.5 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#FAF9F6] transition-transform duration-300 hover:-translate-y-0.5" data-testid="horizon-hero-experiences-btn">
              Explore experiences
            </Link>
            <button onClick={() => navigate("/horizon/gate")} className="rounded-full border border-[#FAF9F6]/40 px-7 py-3.5 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#FAF9F6] transition-colors duration-300 hover:bg-[#FAF9F6] hover:text-[#0A0A0A]" data-testid="horizon-hero-member-btn">
              Member access
            </button>
          </motion.div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="mx-auto max-w-[1100px] px-5 py-20 text-center md:px-10 md:py-28">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }}>
          <Compass size={30} strokeWidth={1.25} className="mx-auto text-[#C05C42]" />
          <p className="mt-8 font-serif text-[clamp(1.6rem,3.5vw,2.6rem)] font-light italic leading-snug text-[#0A0A0A]/85">
            "We do not sell trips. We compose a life lived across cultures — with the
            discretion of a private club and the curiosity of a great review."
          </p>
        </motion.div>
      </section>

      {/* Tiers */}
      <section className="bg-[#FAF9F6] px-5 py-20 md:px-10 md:py-28" data-testid="horizon-tiers">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-14 text-center">
            <p className="font-body text-[0.7rem] uppercase tracking-[0.28em] text-[#C05C42]">Membership</p>
            <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight md:text-5xl">Three tiers of belonging</h2>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {HORIZON_TIERS.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-8%" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease }}
                className={`flex flex-col rounded-2xl border bg-[#FAF9F6] p-8 ${
                  t.featured ? "border-[#C05C42] shadow-[0_20px_60px_-30px_rgba(192,92,66,0.6)]" : "border-[#0A0A0A]/12"
                }`}
                data-testid={`tier-${t.id}`}
              >
                {t.featured && (
                  <span className="mb-5 inline-block w-fit rounded-full bg-[#C05C42] px-3 py-1 font-body text-[0.55rem] uppercase tracking-[0.2em] text-[#FAF9F6]">
                    Most chosen
                  </span>
                )}
                <p className="font-body text-[0.62rem] uppercase tracking-[0.22em] text-[#C05C42]">{t.kicker}</p>
                <h3 className="mt-3 font-serif text-3xl font-semibold tracking-tight">{t.name}</h3>
                <p className="mt-2 font-body text-[0.7rem] uppercase tracking-[0.18em] text-[#0A0A0A]/45">{t.price}</p>
                <p className="mt-5 font-body text-sm leading-relaxed text-[#0A0A0A]/65">{t.body}</p>
                <ul className="mt-6 flex-1 space-y-3 border-t border-[#0A0A0A]/10 pt-6">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 font-body text-sm text-[#0A0A0A]/70">
                      <Check size={15} className="mt-0.5 shrink-0 text-[#C05C42]" /> {p}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setWaitlist(t.name)}
                  className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-body text-[0.65rem] uppercase tracking-[0.2em] transition-transform duration-300 hover:-translate-y-0.5 ${
                    t.featured ? "bg-[#C05C42] text-[#FAF9F6]" : "bg-[#0A0A0A] text-[#FAF9F6]"
                  }`}
                  data-testid={`tier-apply-${t.id}`}
                >
                  Request the waitlist
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <AlcottFooter onJoin={() => setWaitlist("The Horizon Circle")} />
      <HorizonWaitlist open={!!waitlist} onClose={() => setWaitlist(null)} context={waitlist} />
    </div>
  );
};

export default Horizon;
